import type { QuerySpec } from '@helios/powersync-client-core';

/**
 * Row shape returned by qPatientsList / qFavoritePatientsList.
 * patients LEFT JOIN first identifier + LEFT JOIN user_patient_favorites.
 */
export interface PatientListRow {
  id: string;
  patient_number: string | null;
  full_name: string | null;
  birth_date: string | null;
  sex: string | null;
  status: string | null;
  last_visited_at: string | null;
  updated_at: string | null;
  id_type: string | null;
  id_value: string | null;
  is_favorite: number | null; // 1 or null
}

/**
 * QuerySpec for the All Patients list.
 * Joins a deterministic primary identifier and per-user favorite status.
 * Supports search, status filter, and sort — all pushed into SQL.
 */
export function qPatientsList(opts: {
  userId?: string;
  search?: string;
  status?: string;
  sort?: 'last-visit' | 'name-az';
}): QuerySpec<string[]> {
  const whereClauses: string[] = [];
  const params: string[] = [];

  // userId param for the favorites LEFT JOIN (always first param)
  params.push(opts.userId ?? '');

  if (opts.status) {
    whereClauses.push('p.status = ?');
    params.push(opts.status);
  }

  if (opts.search?.trim()) {
    const like = `%${opts.search.trim()}%`;
    whereClauses.push('(p.full_name LIKE ? OR p.patient_number LIKE ? OR pi.id_value LIKE ?)');
    params.push(like, like, like);
  }

  const where = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';

  const orderBy =
    opts.sort === 'name-az'
      ? 'ORDER BY p.full_name ASC'
      : 'ORDER BY COALESCE(p.last_visited_at, p.updated_at) DESC';

  const sql = `
    SELECT
      p.id, p.patient_number, p.full_name, p.birth_date, p.sex,
      p.status, p.last_visited_at, p.updated_at,
      pi.id_type, pi.id_value,
      (fav.id IS NOT NULL) as is_favorite
    FROM patients p
    LEFT JOIN patient_identifiers pi
      ON pi.id = (
        SELECT id FROM patient_identifiers
        WHERE patient_id = p.id
        ORDER BY id LIMIT 1
      )
    LEFT JOIN user_patient_favorites fav
      ON fav.patient_id = p.id AND fav.user_id = ?
    ${where}
    ${orderBy}
  `.trim();

  return {
    key: [
      'patients',
      'list',
      opts.userId ?? '',
      opts.search ?? '',
      opts.status ?? '',
      opts.sort ?? '',
    ],
    sql,
    params,
    watch: ['patients', 'patient_identifiers', 'user_patient_favorites'],
  };
}

/**
 * QuerySpec for the Favorites page — only patients the current user has favorited.
 */
/**
 * Row shape for duplicate check — patient joined with contact.
 */
export interface PatientDuplicateRow {
  id: string;
  full_name: string | null;
  patient_number: string | null;
  mobile_country_code: string | null;
  mobile_number: string | null;
}

/**
 * Find patients matching a given mobile_country_code + mobile_number.
 * Used for soft duplicate check during Quick Patient creation.
 */
export function qPatientDuplicateByMobile(
  mobileCountryCode: string,
  mobileNumber: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patients', 'duplicate-mobile', mobileCountryCode, mobileNumber],
    sql: `SELECT p.id, p.full_name, p.patient_number, c.mobile_country_code, c.mobile_number
      FROM patients p
      INNER JOIN patient_contacts c ON c.patient_id = p.id
      WHERE c.mobile_country_code = ? AND c.mobile_number = ?
      LIMIT 5`,
    params: [mobileCountryCode, mobileNumber],
    watch: ['patients', 'patient_contacts'],
  };
}

// ─── Drawer-specific row (includes contact info) ───

export interface PatientDrawerRow extends PatientListRow {
  phone: string | null;
  email: string | null;
  photo_url: string | null;
}

export type DrawerTab = 'all' | 'favorites' | 'today' | 'new' | 'recent';

/**
 * Query for the Open Patient drawer — includes contact phone/email.
 * Tab determines the WHERE / JOIN / ORDER logic.
 */
export function qPatientsDrawerList(opts: {
  userId: string;
  search?: string;
  tab: DrawerTab;
}): QuerySpec<string[]> {
  const params: string[] = [];
  const whereClauses: string[] = [];
  let extraJoins = '';
  // Default ("all" tab): most recently updated patients first.
  let orderBy = 'ORDER BY p.updated_at DESC';

  // userId for favorites LEFT JOIN (always first param)
  params.push(opts.userId);

  if (opts.tab === 'favorites') {
    extraJoins = `INNER JOIN user_patient_favorites fav ON fav.patient_id = p.id AND fav.user_id = ?`;
    params.push(opts.userId);
    orderBy = 'ORDER BY fav.created_at DESC';
  }

  if (opts.tab === 'today') {
    const today = new Date().toISOString().slice(0, 10);
    whereClauses.push(`(date(p.last_visited_at) = ? OR date(p.updated_at) = ?)`);
    params.push(today, today);
  }

  if (opts.tab === 'new') {
    // Created in last 7 days (use updated_at since created_at not synced)
    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    whereClauses.push(`p.updated_at >= ?`);
    params.push(sevenDaysAgo);
    orderBy = 'ORDER BY p.updated_at DESC';
  }

  if (opts.tab === 'recent') {
    whereClauses.push(`p.last_visited_at IS NOT NULL`);
    orderBy = 'ORDER BY p.last_visited_at DESC';
  }

  if (opts.search?.trim()) {
    const like = `%${opts.search.trim()}%`;
    whereClauses.push(
      '(p.full_name LIKE ? OR p.patient_number LIKE ? OR pi.id_value LIKE ? OR c.mobile_number LIKE ? OR c.email LIKE ?)',
    );
    params.push(like, like, like, like, like);
  }

  const where = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';

  const sql = `
    SELECT
      p.id, p.patient_number, p.full_name, p.birth_date, p.sex,
      p.status, p.last_visited_at, p.updated_at, p.photo_url,
      pi.id_type, pi.id_value,
      (ufav.id IS NOT NULL) as is_favorite,
      CASE WHEN c.mobile_country_code IS NOT NULL AND c.mobile_number IS NOT NULL
        THEN c.mobile_country_code || ' ' || c.mobile_number
        ELSE NULL END as phone,
      c.email
    FROM patients p
    LEFT JOIN patient_identifiers pi
      ON pi.id = (
        SELECT id FROM patient_identifiers
        WHERE patient_id = p.id
        ORDER BY id LIMIT 1
      )
    LEFT JOIN patient_contacts c
      ON c.id = (
        SELECT id FROM patient_contacts
        WHERE patient_id = p.id
        ORDER BY id LIMIT 1
      )
    LEFT JOIN user_patient_favorites ufav
      ON ufav.patient_id = p.id AND ufav.user_id = ?
    ${extraJoins}
    ${where}
    ${orderBy}
    LIMIT 50
  `.trim();

  return {
    key: ['patients', 'drawer', opts.tab, opts.userId, opts.search ?? ''],
    sql,
    params,
    watch: ['patients', 'patient_identifiers', 'patient_contacts', 'user_patient_favorites'],
  };
}

export function qFavoritePatientsList(opts: {
  /** Scope to one user's favorites. Empty string = favorites of ALL users (deduped). */
  userId: string;
  search?: string;
  status?: string;
  sort?: 'last-visit' | 'name-az';
}): QuerySpec<string[]> {
  const whereClauses: string[] = [];
  const params: string[] = [];

  // Favorites join. When userId is given, scope to that user. When empty
  // ("all users"), collapse to one row per patient favorited by anyone, using
  // the most recent favorite time so the default sort still makes sense.
  let favJoin: string;
  if (opts.userId) {
    favJoin = `INNER JOIN user_patient_favorites fav
      ON fav.patient_id = p.id AND fav.user_id = ?`;
    params.push(opts.userId); // first param, matches the join's ?
  } else {
    favJoin = `INNER JOIN (
        SELECT patient_id, MAX(created_at) AS created_at
        FROM user_patient_favorites
        GROUP BY patient_id
      ) fav ON fav.patient_id = p.id`;
  }

  if (opts.status) {
    whereClauses.push('p.status = ?');
    params.push(opts.status);
  }

  if (opts.search?.trim()) {
    const like = `%${opts.search.trim()}%`;
    whereClauses.push('(p.full_name LIKE ? OR p.patient_number LIKE ? OR pi.id_value LIKE ?)');
    params.push(like, like, like);
  }

  const where = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';

  const orderBy =
    opts.sort === 'name-az' ? 'ORDER BY p.full_name ASC' : 'ORDER BY fav.created_at DESC';

  const sql = `
    SELECT
      p.id, p.patient_number, p.full_name, p.birth_date, p.sex,
      p.status, p.last_visited_at, p.updated_at,
      pi.id_type, pi.id_value,
      1 as is_favorite
    FROM patients p
    ${favJoin}
    LEFT JOIN patient_identifiers pi
      ON pi.id = (
        SELECT id FROM patient_identifiers
        WHERE patient_id = p.id
        ORDER BY id LIMIT 1
      )
    ${where}
    ${orderBy}
  `.trim();

  return {
    key: [
      'patients',
      'favorites',
      opts.userId,
      opts.search ?? '',
      opts.status ?? '',
      opts.sort ?? '',
    ],
    sql,
    params,
    watch: ['patients', 'patient_identifiers', 'user_patient_favorites'],
  };
}
